import { getBorders } from '@/libs/pattern/borderUtils'
import { BoxDef } from '@/libs/pattern/boxDef'
import * as utils from '@/libs/pattern/utils'
import { BoxPosition } from '@/generated/model/boxPosition'
import { Sequence } from '@/generated/model/sequence'
import { Borders } from '@/generated/model/gripper'
import { ProgramSettings } from '@/generated/model/programSettings'
import { directionX, directionY } from '@/libs/pattern/utils'

type LabelSide = 'FRONT' | 'RIGHT' | 'BACK' | 'LEFT'

const labelSides: LabelSide[] = ['FRONT', 'RIGHT', 'BACK', 'LEFT']
const customInfeedRotations = [90, 180, 270]
const splitLabelPosition = (labelPosition: string): LabelSide[] => {
  switch (labelPosition) {
    case 'FRONT_RIGHT_CORNER':
      return ['FRONT', 'RIGHT']
    case 'RIGHT_BACK_CORNER':
      return ['RIGHT', 'BACK']
    case 'BACK_LEFT_CORNER':
      return ['BACK', 'LEFT']
    case 'LEFT_FRONT_CORNER':
      return ['LEFT', 'FRONT']
    case 'RIGHT':
    case 'BACK':
    case 'LEFT':
    case 'FRONT':
    default:
      return [labelPosition as LabelSide]
  }
}

const combineLabelPosition = (sides: LabelSide[]): ProgramSettings.LabelPositionEnum => {
  const unique = Array.from(new Set(sides))
  if (unique.length === 1) {
    return unique[0] as ProgramSettings.LabelPositionEnum
  }
  const indexes = unique.map(side => labelSides.indexOf(side)).sort((a, b) => a - b)
  if (indexes.includes(0) && indexes.includes(3)) {
    return ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER
  }
  const first = labelSides[indexes[0]]
  const second = labelSides[indexes[1]]
  return `${first}_${second}_CORNER` as ProgramSettings.LabelPositionEnum
}

const rotateLabelSide = (side: LabelSide, rotation: number): LabelSide => {
  const steps = (((Math.round(rotation / 90) % 4) + 4) % 4)
  const index = labelSides.indexOf(side)
  return labelSides[(index + steps) % labelSides.length]
}

export const getCustomInfeedRotation = (sequence?: Sequence): number => {
  const rotation = Number(sequence && sequence.customInfeedRotation)
  return customInfeedRotations.includes(rotation) ? rotation : 0
}

export const hasCustomInfeedRotation = (sequence?: Sequence): boolean => {
  return sequence != null && sequence.customInfeedRotation != null && customInfeedRotations.includes(Number(sequence.customInfeedRotation))
}

const getCustomInfeedLabelDelta = (customInfeedRotation: number): number => {
  const rotation = Number(customInfeedRotation)
  return rotation === 90 || rotation === 270 ? 180 : 0
}

const mirrorConveyorSide = (side: LabelSide, conveyorConfiguration: string | undefined): LabelSide => {
  if (conveyorConfiguration !== 'LEFT' && conveyorConfiguration !== 'CENTER_LEFT') {
    return side
  }
  if (side === 'RIGHT') {
    return 'LEFT'
  }
  if (side === 'LEFT') {
    return 'RIGHT'
  }
  return side
}

const mapCustomSolutionConveyorSide = (side: LabelSide): LabelSide => {
  if (side === 'RIGHT') {
    return 'LEFT'
  }
  if (side === 'LEFT') {
    return 'RIGHT'
  }
  return side
}

export const getCustomSolutionConveyorLabelPosition = (
  labelPosition: ProgramSettings.LabelPositionEnum | undefined,
  customInfeedRotation: number = 0
): ProgramSettings.LabelPositionEnum => {
  if (labelPosition == null || labelPosition === ProgramSettings.LabelPositionEnum.IGNORE) {
    return ProgramSettings.LabelPositionEnum.IGNORE
  }
  return combineLabelPosition(splitLabelPosition(labelPosition)
    .map(side => rotateLabelSide(side, customInfeedRotation))
    .map(mapCustomSolutionConveyorSide))
}

const mapConveyorSideToInitialPalletSide = (side: LabelSide): LabelSide => {
  if (side === 'FRONT') {
    return 'BACK'
  }
  if (side === 'BACK') {
    return 'FRONT'
  }
  if (side === 'RIGHT') {
    return 'LEFT'
  }
  return 'RIGHT'
}

export const calculateCustomLabelPosition = (
  labelPosition: ProgramSettings.LabelPositionEnum | undefined,
  _boxOrientationOnConveyor: ProgramSettings.BoxOrientationOnConveyorEnum | undefined,
  conveyorConfiguration: string | undefined,
  boxRotation: number,
  pickRotation: number,
  placeRotation: number,
  customInfeedRotation: number = 0
): ProgramSettings.LabelPositionEnum => {
  if (labelPosition == null || labelPosition === ProgramSettings.LabelPositionEnum.IGNORE) {
    return ProgramSettings.LabelPositionEnum.IGNORE
  }

  // The configured label side is defined in the conveyor reference frame. When the box is shown on
  // the pallet, every side maps to the opposite local box side. Pick/place then rotates that side
  // together with the placed box geometry. Custom infeed 90/270 can only move the label to the
  // opposite matching face here, so a long-side label never becomes a short-side label.
  const localDelta = customInfeedRotation
    ? placeRotation - pickRotation - customInfeedRotation - boxRotation + getCustomInfeedLabelDelta(customInfeedRotation)
    : placeRotation - pickRotation - boxRotation
  return combineLabelPosition(splitLabelPosition(labelPosition)
    .map(side => mirrorConveyorSide(side, conveyorConfiguration))
    .map(mapConveyorSideToInitialPalletSide)
    .map(side => rotateLabelSide(side, localDelta)))
}

export const findSequenceByBoxId = (id: number, sequences: Sequence[]): Sequence | undefined => {
  const boxId = Number(id)
  return sequences.find(s => s.pick!.some(p => (p.boxesGroup || []).some(box => Number(box) === boxId)))
}

export const getSequenceBoxNumbers = (sequences?: Sequence[]): Map<number, number> => {
  const boxNumbers = new Map<number, number>()
  if (!sequences) {
    return boxNumbers
  }
  sequences.forEach(sequence => {
    (sequence.pick || []).flatMap(pick => pick.boxesGroup || []).forEach(boxId => {
      const id = Number(boxId)
      if (!boxNumbers.has(id)) {
        boxNumbers.set(id, boxNumbers.size + 1)
      }
    })
  })
  return boxNumbers
}

export function createSequence (id: number) {
  const sequence: Sequence = {
    id: id,
    gripperOffsetX: 0,
    gripperOffsetY: 0,
    pick: [
      {
        id: 1,
        boxesGroup: [],
        zRot: 0
      }
    ],
    place: [
      {
        id: 1,
        zRot: 0
      }
    ]
  }
  return sequence
}

export const allBoxesInSequence = (sequences: Sequence[], boxes: BoxPosition[]): boolean => {
  if (!sequences || sequences.length === 0) {
    return (!boxes || boxes.length === 0)
  }
  const boxesInSequences = sequences.flatMap(s => s.pick!.flatMap(p => p.boxesGroup))
  return boxes.map(b => b.id).every(id => boxesInSequences.includes(id))
}

const getGripperCenter = (rotation: number, border: Borders, gripperLength: number, gripperWidth: number): {x: number, y: number} => {
  let x: number = 0
  let y: number = 0
  const hgl = gripperLength * 0.5
  const hgw = gripperWidth * 0.5
  switch (rotation) {
    case 0: // gripper edge attached to highest X and lowest Y of rectangle
      x = Math.max(border.left, border.right) - hgl
      y = Math.min(border.top, border.bottom) + hgw
      break
    case 90: // gripper edge attached to highest X and highest Y of rectangle
      x = Math.max(border.left, border.right) - hgw
      y = Math.max(border.top, border.bottom) - hgl
      break
    case 180: // gripper edge attached to lowest X and highest Y of rectangle
      x = Math.min(border.left, border.right) + hgl
      y = Math.max(border.top, border.bottom) - hgw
      break
    case 270: // gripper edge attached to lowest X and lowest Y of rectangle
      x = Math.min(border.left, border.right) + hgw
      y = Math.min(border.top, border.bottom) + hgl
      break
  }
  return { x: x, y: y }
}

export const calculatePlacePosition = (placeRotation: number, placeGroup: number[], positions: BoxPosition[], boxDef: BoxDef, gripperLength: number, gripperWidth: number): { x: number, y: number } => {
  // box rectangle on pallet
  const group = positions.filter(p => placeGroup.includes(p.id!))
  const groupBorder = getBorders (group, boxDef, true)
  // console.log('calculatePlacePosition - groupBorder: ', JSON.stringify(groupBorder))
  const cg = getGripperCenter(placeRotation, groupBorder, gripperLength, gripperWidth)
  // console.log('calculatePlacePosition - x, y: ', cg.x, cg.y)
  return { x: Math.round(cg.x), y: Math.round(cg.y) }
}

export const calculatePickPosition = (pickRotation: number, pickGroup: number[], positions: BoxPosition[], boxDef: BoxDef, gripperLength: number, gripperWidth: number, programSettings: ProgramSettings)
  : { x: number, y: number } => {
  // box rectangle on conveyor
  const isCR = programSettings.conveyorConfiguration === 'CENTER_RIGHT'
  const rectLength = boxDef.width * pickGroup.length
  const rectWidth = boxDef.height
  // console.log('calculatePickPosition: ', pickRotation, JSON.stringify(pickGroup), programSettings.boxOrientationOnConveyor, programSettings.conveyorConfiguration)
  const rectBorder = {
    top: (isCR) ? 0 : -rectWidth,
    left: rectLength,
    right: 0,
    bottom: (isCR) ? rectWidth : 0
  }
  // console.log('calculatePickPosition - border: ', JSON.stringify(rectBorder), programSettings.conveyorConfiguration)
  const cg = getGripperCenter(pickRotation, rectBorder, gripperLength, gripperWidth)
  // console.log('calculatePickPosition - x, y: ', cg.x, cg.y)
  return { x: Math.round(cg.x), y: Math.round(cg.y) }
}

export const calculatePickPosition2 = (sequence: Sequence, positions: BoxPosition[], boxDef: BoxDef, gripperLength: number, gripperWidth: number, programSettings: ProgramSettings, conveyorBoxDef: BoxDef = boxDef)
  : { x: number, y: number } => {
  let x: number
  let y: number
  const pick2 = sequence.pick![1]
  // offset box rectangle center from gripper center (place x,y) - pallet coordinates
  const group = positions.filter(p => pick2.boxesGroup!.includes(p.id!))
  const groupBorder = getBorders (group, boxDef, true)
  // console.log('pickPos2 - group2: ', JSON.stringify(groupBorder))
  // imaginary box center
  const ibcX = groupBorder.left - (groupBorder.left - groupBorder.right) * 0.5
  const ibcY = groupBorder.bottom - (groupBorder.bottom - groupBorder.top) * 0.5
  // console.log('pickPos2 - imaginary box center: ', ibcX, ibcY)
  // offset
  const offsetX = ibcX - sequence.place![0].x!
  const offsetY = ibcY - sequence.place![0].y!
  // console.log('pickPos2 - box-gripper-center-offset: ', offsetX, offsetY, sequence.place![0].x!, sequence.place![0].y!)

  // box rectangle center "on conveyor" (conveyor frame coordinates)
  const isCR = programSettings.conveyorConfiguration === 'CENTER_RIGHT'
  const boxLength = conveyorBoxDef.width * pick2.boxesGroup!.length
  const boxWidth = conveyorBoxDef.height
  // console.log('calculatePickPosition2: ', pick2.zRot, JSON.stringify(pick2.boxesGroup), programSettings.boxOrientationOnConveyor, programSettings.conveyorConfiguration)
  const ibcCX = boxLength * 0.5
  const ibcCY = (isCR) ? boxWidth * 0.5 : -boxWidth * 0.5
  // console.log('calculatePickPosition2 - imaginary box center (conveyor): ', ibcCX, ibcCY, programSettings.conveyorConfiguration)
  // center of gripper (conveyor frame coordinates)
  x = ibcCX + offsetX * directionX(pick2.zRot!)
  y = ibcCY + offsetY * directionY(pick2.zRot!)
  // console.log('calculatePickPosition2 - x, y: ', x, y)
  return { x: Math.round(x), y: Math.round(y) }
}

export const getSequenceGripperBorder = (placeRotation: number, placeX: number, placeY: number, gripperLength: number, gripperWidth: number, palletRotation: number): Borders => {
  // console.log('getSequenceGripperBorder (1): ', placeRotation, placeX, placeY, palletRotation)
  const b: Borders = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  }
  const rotation = (palletRotation + placeRotation) % 360
  const gl = (gripperLength * utils.projX (rotation) + gripperWidth * utils.projY (rotation))
  const gw = (gripperWidth * utils.projX (rotation) + gripperLength * utils.projY (rotation))
  const distanceX = gl * 0.5
  const distanceY = gw * 0.5
  // console.log('getSequenceGripperBorder (2): ', gl, gw, distanceX, distanceY, rotation)
  // gripper border
  b.left = placeX + (utils.projX (rotation) * distanceX - utils.projY (rotation) * distanceX)
  b.right = placeX - (utils.projX (rotation) * distanceX - utils.projY (rotation) * distanceX)
  b.top = placeY - (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  b.bottom = placeY + (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  // console.log('getSequenceGripperBorder (3): ', JSON.stringify(b))
  return b
}
